import { useCallback, useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, Link2, RefreshCw, Unplug } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  disconnectGoogleCalendar,
  getCalendarStatus,
  startGoogleCalendarConnect,
  syncAllCalendarDeadlines,
  updateCalendarPrefs,
  type CalendarConnection,
  type CalendarEventRow,
} from "@/lib/partnerGoogleCalendar";
import { cn } from "@/lib/utils";

export function PartnerGoogleCalendarCard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [connection, setConnection] = useState<CalendarConnection | null>(null);
  const [events, setEvents] = useState<CalendarEventRow[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const res = await getCalendarStatus();
    setLoading(false);

    if (res.error === "not_configured") {
      setNotConfigured(true);
      setConnected(false);
      setMessage(res.message || null);
      return;
    }

    setNotConfigured(false);
    setConnected(Boolean(res.connected));
    setConnection(res.connection || null);
    setEvents(res.events || []);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (searchParams.get("calendar") === "connected") {
      toast.success("Google Calendar connected.");
      const next = new URLSearchParams(searchParams);
      next.delete("calendar");
      setSearchParams(next, { replace: true });
      void refresh();
    }
  }, [searchParams, setSearchParams, refresh]);

  const handleConnect = async () => {
    setBusy(true);
    const res = await startGoogleCalendarConnect();
    setBusy(false);

    if (res.error === "not_configured") {
      setNotConfigured(true);
      toast.error(res.message || "Google Calendar is not configured yet.");
      return;
    }
    if (!res.ok || !res.url) {
      toast.error(res.message || "Could not start Google Calendar connect.");
      return;
    }
    window.location.href = res.url;
  };

  const handleDisconnect = async () => {
    if (!confirm("Disconnect Google Calendar? Synced events can be removed from your calendar.")) {
      return;
    }
    setBusy(true);
    const res = await disconnectGoogleCalendar();
    setBusy(false);
    if (!res.ok) {
      toast.error(res.message || "Could not disconnect.");
      return;
    }
    toast.success("Google Calendar disconnected.");
    await refresh();
  };

  const handleSyncAll = async () => {
    setBusy(true);
    const res = await syncAllCalendarDeadlines();
    setBusy(false);
    if (!res.ok) {
      toast.error(res.message || "Sync failed.");
      return;
    }
    toast.success(`Synced ${res.synced ?? 0} program deadline(s).`);
    await refresh();
  };

  const togglePref = async (key: "sync_deadlines" | "sync_interviews", value: boolean) => {
    setBusy(true);
    const res = await updateCalendarPrefs({ [key]: value });
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error || "Could not update preferences.");
      return;
    }
    setConnection((prev) => (prev ? { ...prev, [key]: value } : prev));
    toast.success("Preferences saved.");
  };

  return (
    <div className="rounded-lg border border-[#d0d7de] bg-white p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-[#1f2328] flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-[#00573C]" />
            Google Calendar
          </h3>
          <p className="text-sm text-[#656d76] mt-1">
            Sync program deadlines and interview reminders to your Google Calendar.
            Interview events include a Google Meet link automatically.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="h-7 w-7 rounded-full border-2 border-[#00573C] border-t-transparent animate-spin" />
        </div>
      ) : notConfigured ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 space-y-2">
          <p className="font-semibold">Almost ready — Google Cloud secrets needed</p>
          <p>
            Add <code className="text-xs">GOOGLE_CALENDAR_CLIENT_ID</code> and{" "}
            <code className="text-xs">GOOGLE_CALENDAR_CLIENT_SECRET</code> as Supabase Edge Function
            secrets for <code className="text-xs">partner-google-calendar</code>, then enable the
            Calendar API and add redirect URI:
          </p>
          <code className="block text-xs break-all bg-white/70 rounded px-2 py-1 border border-amber-100">
            {window.location.origin}/partners/calendar/callback
          </code>
          {message ? <p className="text-xs opacity-80">{message}</p> : null}
        </div>
      ) : !connected ? (
        <div className="space-y-4">
          <ul className="text-sm text-[#656d76] space-y-1.5 list-disc pl-5">
            <li>Program application deadlines become all-day calendar events</li>
            <li>Moving an applicant to Interview creates a reminder you can reschedule</li>
            <li>You can disconnect anytime</li>
          </ul>
          <Button
            type="button"
            disabled={busy}
            onClick={handleConnect}
            className="rounded-md bg-[#00573C] hover:bg-[#004530] text-white gap-2"
          >
            <Link2 className="h-4 w-4" />
            {busy ? "Connecting…" : "Connect Google Calendar"}
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#d0d7de] bg-[#f6f8fa] px-3 py-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-[#1f2328]">
              Connected{connection?.google_email ? ` as ${connection.google_email}` : ""}
            </span>
            {connection?.last_synced_at ? (
              <span className="text-xs text-[#656d76]">
                · Last sync {new Date(connection.last_synced_at).toLocaleString()}
              </span>
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="text-[#1f2328]">Sync program deadlines</span>
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#00573C]"
                checked={connection?.sync_deadlines !== false}
                disabled={busy}
                onChange={(e) => void togglePref("sync_deadlines", e.target.checked)}
              />
            </label>
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="text-[#1f2328]">Sync interview reminders</span>
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#00573C]"
                checked={connection?.sync_interviews !== false}
                disabled={busy}
                onChange={(e) => void togglePref("sync_interviews", e.target.checked)}
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={handleSyncAll}
              className="rounded-md gap-2"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} />
              Sync all deadlines
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={handleDisconnect}
              className="rounded-md gap-2 text-rose-700 border-rose-200 hover:bg-rose-50"
            >
              <Unplug className="h-3.5 w-3.5" />
              Disconnect
            </Button>
          </div>

          {events.length > 0 ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#656d76] mb-2">
                Upcoming synced events
              </p>
              <ul className="space-y-2">
                {events.slice(0, 6).map((ev) => (
                  <li
                    key={ev.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-[#d0d7de] bg-white px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-[#1f2328]">{ev.title || "Event"}</p>
                      <p className="text-xs text-[#656d76] capitalize">{ev.event_type}</p>
                    </div>
                    <span className="text-xs text-[#656d76] shrink-0">
                      {ev.event_start
                        ? new Date(ev.event_start).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })
                        : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
